import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

function StatCard({ label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

function AlertCard({ message, type }) {
  const styles = {
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
  }
  return (
    <div className={`border rounded-lg px-4 py-3 text-sm ${styles[type] || styles.info}`}>
      {message}
    </div>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trusts, setTrusts] = useState([])
  const [transactions, setTransactions] = useState([])
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)

    // Fetch profile
    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
    setProfile(profileData)

    // Fetch trusts the user has access to
    const { data: memberData } = await supabase
      .from('trust_members')
      .select('*, trusts(*)')
      .eq('user_id', user.id)
    
    const trustList = memberData?.map(m => m.trusts).filter(Boolean) || []
    setTrusts(trustList)

    // Fetch recent transactions across all trusts
    if (trustList.length > 0) {
      const trustIds = trustList.map(t => t.id)
      const { data: txData } = await supabase
        .from('transactions')
        .select('*, accounts(name), trusts(name)')
        .in('trust_id', trustIds)
        .order('date', { ascending: false })
        .limit(5)
      setTransactions(txData || [])
    }

    setLoading(false)
  }

  // Calculate summary stats
  const totalPrincipal = trusts.reduce((sum, t) => {
    return sum
  }, 0)

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    })
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">

        {/* Welcome Banner */}
        <div className="bg-blue-600 rounded-xl p-6 text-white">
          <h2 className="text-xl font-bold">
            Welcome back{profile?.full_name ? `, ${profile.full_name}` : ''}
          </h2>
          <p className="text-blue-100 text-sm mt-1">
            Here's an overview of your trust activity
          </p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Active Trusts"
            value={trusts.filter(t => t?.status === 'active').length}
            sub="trusts under management"
            color="text-blue-600"
          />
          <StatCard
            label="Total Principal"
            value={formatCurrency(0)}
            sub="across all trusts"
            color="text-gray-900"
          />
          <StatCard
            label="Total Income"
            value={formatCurrency(0)}
            sub="across all trusts"
            color="text-green-600"
          />
        </div>

        {/* Alerts */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Alerts
          </h3>
          {trusts.length === 0 ? (
            <AlertCard
              message="You have no trusts yet. Create your first trust to get started."
              type="info"
            />
          ) : (
            <AlertCard
              message="All trusts are up to date."
              type="success"
            />
          )}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Recent Transactions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Recent Transactions</h3>
              <button
                onClick={() => navigate('/transactions')}
                className="text-sm text-blue-600 hover:underline"
              >
                View all
              </button>
            </div>
            {transactions.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">
                No transactions yet
              </p>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{tx.description}</p>
                      <p className="text-xs text-gray-400">{tx.trusts?.name} · {formatDate(tx.date)}</p>
                    </div>
                    <span className={`text-sm font-semibold ${
                      tx.type === 'income' ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* My Trusts */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">My Trusts</h3>
              <button
                onClick={() => navigate('/trusts')}
                className="text-sm text-blue-600 hover:underline"
              >
                View all
              </button>
            </div>
            {trusts.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-400 mb-3">No trusts yet</p>
                <button
                  onClick={() => navigate('/trusts/new')}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                >
                  + Create First Trust
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {trusts.slice(0, 4).map((trust) => (
                  <div
                    key={trust?.id}
                    onClick={() => navigate(`/trusts/${trust?.id}`)}
                    className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 rounded px-2"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">{trust?.name}</p>
                      <p className="text-xs text-gray-400">{trust?.state_of_domicile} · {trust?.status}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      trust?.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {trust?.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}